import { LoadingIndicatorComponent } from '../../components/loading-indicator/loading-indicator.component';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription, interval, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  AerosysGatewayService,
  AerosysItemPendenteLegado,
  AerosysItemPendenteConferenciaLegado
} from '../../services/aerosys-gateway.service';
import { SankhyaGatewayService } from '../../services/sankhya-gateway.service';
import { PermissaoAcessoService } from '../../services/permissao-acesso.service';

/**
 * Modelo unificado de item pendente, cobrindo tanto o Sistema Legado
 * (Aerosys, chave venId/ivdId) quanto o fluxo nativo Sankhya (chave
 * nunota/sequencia) -- ver PendenciaSeparacaoComponent (GPL-Integracao),
 * SeparacaoLegadoService e ConferenciaLegadoService (Aerosys) para as
 * origens dos dados.
 */
interface ItemPendenteUnificado {
  origem: 'aerosys' | 'sankhya';
  chaveVenda: number;   // venId (aerosys) ou nunota (sankhya)
  chaveItem: number;    // ivdId (aerosys) ou sequencia (sankhya)
  /** Em qual etapa a pendência foi sinalizada (Separação ou Conferência) --
   *  decide qual endpoint resolve (ver confirmarResolucao). Relevante pras
   *  DUAS origens: tanto o Sankhya quanto o Sistema Legado têm pendência
   *  própria em cada etapa (tabelas/endpoints diferentes -- ver
   *  SeparacaoPendenciaLegado x ConferenciaPendenciaLegado no Aerosys). */
  etapa?: 'separacao' | 'conferencia';
  gplCodpend?: number | null;
  numeroVenda: string;
  pn?: string | null;
  descricao?: string | null;
  quantidade?: number | null;
  unidade?: string | null;
  local?: string | null;
  cd?: string | null;
  nomeCliente?: string | null;
  estoque?: number | null;
  sinalizadoPor?: string | null;
  dataSinalizacao?: string | null;
  observacao?: string | null;
}

/**
 * ============================================================================
 *  INTEGRAÇÃO GPL — "Itens Pendentes" (fila global "Item no outro Galpão")
 * ============================================================================
 *  Tela GLOBAL (todas as vendas) com a fila de itens sinalizados como "Item
 *  no outro Galpão" durante a separação OU a conferência -- tanto do Sistema
 *  Legado (Aerosys, ver estoque-separacao-itens-legado.component.ts e
 *  estoque-conferencia-itens-legado.component.ts) quanto do fluxo nativo
 *  Sankhya (ver estoque-separacao-itens.component.ts e
 *  estoque-conferencia-itens.component.ts) -- pedido explícito do cliente: o
 *  galpão vizinho guarda parte do estoque, e sem a antiga folha A4/
 *  prancheta em papel, o app precisa de uma tela própria pra alguém com
 *  tempo livre ver "o que falta buscar" e confirmar quando trouxer.
 *
 *  FONTE ÚNICA POR VEZ (Legado OU Sankhya, nunca os dois juntos) -- mesmo
 *  padrão de exclusividade já usado no Painel de Separação e no Aplicativo
 *  de Separação (ver estoque-separacao.component.ts/paineis-separacao.
 *  component.ts, "legadoAberto"). Aqui isso é ainda mais importante: as
 *  duas fontes são consultas totalmente independentes (Aerosys/Firebird vs
 *  Sankhya/Oracle) -- se buscássemos as duas ao mesmo tempo (ex.: com
 *  forkJoin) e UMA delas falhasse (ex.: GPL_SEPARACAO_PENDENCIA ainda não
 *  publicada no Sankhya, ORA-00942), a tela inteira ficaria sem mostrar
 *  NADA, mesmo pra quem só usa o Legado. Consultando uma fonte de cada vez
 *  isso não acontece: cada lado só é chamado quando o próprio usuário está
 *  olhando pra ele.
 *
 *  Abre por padrão na fonte que a permissão VERAERO indica (mesma lógica de
 *  estoque-separacao.component.ts), mas o usuário pode alternar manualmente
 *  (checkbox no header) -- alguém com a permissão pode querer checar as
 *  pendências nativas também, e vice-versa. Preferência não é persistida
 *  (tela de uso ocasional, o padrão pela permissão já acerta a maioria).
 *
 *  CONFIRMAÇÃO SIMPLES: decisão explícita do cliente -- não exige
 *  identificação/login nenhum pra confirmar que já buscou um item. É só um
 *  diálogo "Confirma que já buscou este item?" (Sim/Não); "Sim" fecha a
 *  pendência e o item some da lista. No fluxo nativo, quem resolveu fica
 *  registrado automaticamente pela sessão Sankhya (AuthenticationInfo); no
 *  Legado, fica sem CODUSU_RESOLUCAO (coluna opcional, é só auditoria
 *  best-effort, não trava nada).
 * ============================================================================
 */
@Component({
  selector: 'app-itens-pendentes-legado',
  standalone: true,
  imports: [LoadingIndicatorComponent, CommonModule, FormsModule],
  templateUrl: './itens-pendentes-legado.component.html',
  styleUrls: ['./itens-pendentes-legado.component.css']
})
export class ItensPendentesLegadoComponent implements OnInit, OnDestroy {
  itens: ItemPendenteUnificado[] = [];
  carregando = false;
  erro = '';
  erroResolver = '';

  /** true = mostrando Sistema Legado (Aerosys); false = mostrando fluxo nativo Sankhya. */
  mostrarLegado = true;

  private autoSub?: Subscription;

  /** Item aguardando confirmação "Já busquei este item?" -- diálogo simples, sem identificação. */
  confirmando: ItemPendenteUnificado | null = null;
  resolvendoChave: string | null = null;

  constructor(
    private gateway: AerosysGatewayService,
    private sankhyaGateway: SankhyaGatewayService,
    private permissaoAcesso: PermissaoAcessoService
  ) {}

  ngOnInit(): void {
    // Abre na fonte que a permissão VERAERO indica -- mesmo padrão do
    // Aplicativo de Separação (estoque-separacao.component.ts).
    this.permissaoAcesso.podeVerVendasLegadoAutorizado().then(pode => {
      this.mostrarLegado = pode;
      this.buscarPendencias(true);
    }).catch(() => {
      this.buscarPendencias(true);
    });

    // Fila global, compartilhada entre vários separadores em paralelo --
    // atualiza sozinha (mesmo padrão de conferencia-pendente.component.ts)
    // pra quem está de olho na tela ver quando alguém resolve um item.
    this.autoSub = interval(15000).subscribe(() => this.buscarPendencias(false));
  }

  ngOnDestroy(): void {
    this.autoSub?.unsubscribe();
  }

  /** Alterna a fonte manualmente e recarrega -- ver comentário de topo sobre exclusividade. */
  alternarFonte(mostrarLegado: boolean): void {
    if (this.mostrarLegado === mostrarLegado) return;
    this.mostrarLegado = mostrarLegado;
    this.itens = [];
    this.buscarPendencias(true);
  }

  private extrairErro(err: any, fallback: string): string {
    const corpo = err?.error;
    if (typeof corpo === 'string' && corpo.trim()) return corpo;
    if (corpo?.erro) return corpo.erro;
    if (corpo?.detail) return corpo.detail;
    if (corpo?.message) return corpo.message;
    if (err?.status === 0) return 'Não foi possível conectar ao servidor.';
    return err?.message ?? fallback;
  }

  buscarPendencias(exibirLoading: boolean): void {
    if (exibirLoading) { this.carregando = true; this.erro = ''; }
    const mostrandoLegado = this.mostrarLegado;

    if (mostrandoLegado) {
      // Sistema Legado: junta as pendências de Separação E Conferência (duas
      // tabelas/endpoints independentes no Aerosys -- ver comentário de topo
      // sobre por que cada etapa é buscada separadamente e com catchError
      // própria, mesmo padrão já usado no lado Sankhya logo abaixo).
      const sepLegado$ = this.gateway.listarPendenciasSeparacaoLegado().pipe(
        map((itens) => ({ ok: true as const, itens: itens ?? [] })),
        catchError((err) => of({ ok: false as const, itens: [] as AerosysItemPendenteLegado[], erro: this.extrairErro(err, 'Erro ao carregar pendências de Separação (Sistema Legado).') }))
      );
      const confLegado$ = this.gateway.listarPendenciasConferenciaLegado().pipe(
        map((itens) => ({ ok: true as const, itens: itens ?? [] })),
        catchError((err) => of({ ok: false as const, itens: [] as AerosysItemPendenteConferenciaLegado[], erro: this.extrairErro(err, 'Erro ao carregar pendências de Conferência (Sistema Legado).') }))
      );

      forkJoin([sepLegado$, confLegado$]).subscribe({
        next: ([resSep, resConf]) => {
          // A fonte pode ter sido trocada enquanto a chamada estava em
          // andamento -- descarta a resposta se não for mais a atual.
          if (this.mostrarLegado !== mostrandoLegado) return;
          this.carregando = false;

          const itensSep  = resSep.ok  ? resSep.itens.map(i  => this._mapLegadoItem(i, 'separacao'))   : [];
          const itensConf = resConf.ok ? resConf.itens.map(i => this._mapLegadoItem(i, 'conferencia')) : [];
          this.itens = [...itensSep, ...itensConf];

          if (!resSep.ok && !resConf.ok) {
            if (exibirLoading || !this.itens.length) {
              this.erro = (resSep as any).erro ?? (resConf as any).erro ?? 'Erro ao carregar itens pendentes do Sistema Legado.';
            }
          } else {
            this.erro = '';
          }
        },
        error: (err: any) => {
          if (this.mostrarLegado !== mostrandoLegado) return;
          this.carregando = false;
          if (exibirLoading || !this.itens.length) {
            this.erro = this.extrairErro(err, 'Erro ao carregar itens pendentes do Sistema Legado.');
          }
        }
      });
    } else {
      // Nativo: junta as pendências de Separação E Conferência (mesma fonte
      // Sankhya, mesmo padrão de exclusividade -- ver comentário de topo).
      // catchError em cada uma evita que a falha de uma etapa (ex.: tabela
      // ainda não publicada) esconda a outra que já está funcionando.
      const mapearItem = (i: any, etapa: 'separacao' | 'conferencia'): ItemPendenteUnificado => ({
        origem: 'sankhya' as const,
        chaveVenda: Number(i.nunota),
        chaveItem: Number(i.sequencia),
        etapa,
        gplCodpend: i.gplCodpend,
        numeroVenda: `#${i.nunota}`,
        pn: i.refforn || (i.codprod != null ? `COD ${i.codprod}` : null),
        descricao: i.descricao,
        quantidade: i.quantidade,
        unidade: i.codvol,
        local: i.local,
        cd: null,
        nomeCliente: i.nomeCliente,
        estoque: null,
        sinalizadoPor: i.sinalizadorNome || null,
        dataSinalizacao: i.dataSinalizacao,
        observacao: i.observacao
      });

      const separacao$ = this.sankhyaGateway.listarPendenciasSeparacao().pipe(
        catchError((err) => of({ sucesso: false, erro: this.extrairErro(err, 'Erro ao carregar pendências de Separação.') }))
      );
      const conferencia$ = this.sankhyaGateway.listarPendenciasConferencia().pipe(
        catchError((err) => of({ sucesso: false, erro: this.extrairErro(err, 'Erro ao carregar pendências de Conferência.') }))
      );

      forkJoin([separacao$, conferencia$]).subscribe({
        next: ([resSep, resConf]: [any, any]) => {
          if (this.mostrarLegado !== mostrandoLegado) return;
          this.carregando = false;

          const sepOk  = !!resSep  && resSep.sucesso  !== false;
          const confOk = !!resConf && resConf.sucesso !== false;

          const itensSep  = sepOk  ? (resSep.itens  ?? []).map((i: any) => mapearItem(i, 'separacao'))  : [];
          const itensConf = confOk ? (resConf.itens ?? []).map((i: any) => mapearItem(i, 'conferencia')) : [];
          this.itens = [...itensSep, ...itensConf];

          if (!sepOk && !confOk) {
            if (exibirLoading || !this.itens.length) {
              this.erro = resSep?.erro ?? resConf?.erro ?? 'Erro ao carregar itens pendentes do Sankhya.';
            }
          } else {
            this.erro = '';
          }
        },
        error: (err: any) => {
          if (this.mostrarLegado !== mostrandoLegado) return;
          this.carregando = false;
          if (exibirLoading || !this.itens.length) {
            this.erro = this.extrairErro(err, 'Erro ao carregar itens pendentes do Sankhya.');
          }
        }
      });
    }
  }

  /** Converte um item pendente do Sistema Legado (Separação OU Conferência --
   *  mesmo formato de campos nas duas DTOs, ver AerosysItemPendenteLegado e
   *  AerosysItemPendenteConferenciaLegado) pro modelo unificado da tela. */
  private _mapLegadoItem(
    i: AerosysItemPendenteLegado | AerosysItemPendenteConferenciaLegado,
    etapa: 'separacao' | 'conferencia'
  ): ItemPendenteUnificado {
    return {
      origem: 'aerosys',
      etapa,
      chaveVenda: i.venId,
      chaveItem: i.ivdId,
      gplCodpend: i.gplCodpend,
      numeroVenda: `#${i.venId}`,
      pn: i.pn,
      descricao: i.descricao,
      quantidade: i.quantidade,
      unidade: null,
      local: i.local,
      cd: i.cd,
      nomeCliente: null,
      estoque: i.estoque,
      sinalizadoPor: i.separadorNome || (i.codusu != null ? `#${i.codusu}` : null),
      dataSinalizacao: i.dataSinalizacao,
      observacao: i.observacao
    };
  }

  /** Primeiro nome só -- mesmo padrão do badge da tela de separação. */
  primeiroNome(nomeCompleto: string | null | undefined): string {
    if (!nomeCompleto) return '';
    return nomeCompleto.trim().split(/\s+/)[0];
  }

  // =======================================================================
  // Confirmar "já busquei este item" -- diálogo simples, sem identificação
  // =======================================================================

  abrirConfirmacao(item: ItemPendenteUnificado): void {
    this.confirmando = item;
    this.erroResolver = '';
  }

  fecharConfirmacao(): void {
    if (this.resolvendoChave != null) return;
    this.confirmando = null;
  }

  confirmarResolucao(): void {
    const item = this.confirmando;
    if (!item) return;
    const chave = `${item.origem}:${item.etapa ?? ''}:${item.chaveVenda}:${item.chaveItem}`;
    if (this.resolvendoChave != null) return;
    this.resolvendoChave = chave;
    this.erroResolver = '';

    const resolver$ = item.origem === 'aerosys'
      ? (item.etapa === 'conferencia'
          ? this.gateway.resolverPendenciaConferenciaLegado(item.chaveVenda, item.chaveItem)
          : this.gateway.resolverPendenciaSeparacaoLegado(item.chaveVenda, item.chaveItem))
      : item.etapa === 'conferencia'
        ? this.sankhyaGateway.resolverPendenciaConferencia(item.chaveVenda, item.chaveItem)
        : this.sankhyaGateway.resolverPendenciaSeparacao(item.chaveVenda, item.chaveItem);

    resolver$.subscribe({
      next: (res: any) => {
        this.resolvendoChave = null;
        if (item.origem === 'sankhya' && res && res.sucesso === false) {
          this.erroResolver = res.erro ?? 'Erro ao confirmar a busca do item.';
          return;
        }
        // Item resolvido -- sai da fila. Remove localmente pra feedback
        // instantâneo, sem esperar o próximo refresh.
        this.itens = this.itens.filter(
          i => !(i.origem === item.origem && i.etapa === item.etapa && i.chaveVenda === item.chaveVenda && i.chaveItem === item.chaveItem)
        );
        this.confirmando = null;
      },
      error: (err: any) => {
        this.resolvendoChave = null;
        this.erroResolver = this.extrairErro(err, 'Erro ao confirmar a busca do item.');
      }
    });
  }
}
